/**
 * VA-28131 step 2: report what the browser already knows, with a time on it.
 *
 * The lowest-false-positive evidence in the whole threat model — no camera, no
 * model, no inference — and almost all of it was being thrown away.
 *
 * `tab_switch` was sent as a bare `{type}` with no timestamp, no duration and no
 * page session, so "the candidate left the tab eleven times" could never be
 * placed against the question being asked. An unplaceable count cannot tell
 * someone checking a notification between questions from someone leaving
 * mid-answer and returning with a solution.
 *
 * Worse: the coding editor BLOCKS copy, cut and paste with `preventDefault()`
 * and then discards the attempt entirely. Someone pasting a solution is the
 * single highest-value signal available, and it left no trace at all.
 *
 * Everything here carries `t` on the RECORDED clock and the page session, the
 * same primitives capture rows use (VA-28116), so the backend's
 * `align_sessions` places these on one timeline with the video and the frames.
 *
 * MODE ISOLATION. This is the shared core: the geometry of what an event IS and
 * when it happened. Each mode supplies its own `send`, which is the only thing
 * that legitimately differs between them.
 */
import { pageSessionId } from '@/recording/upload-client';
let sink = null;
/** Register the active mode's sink. Called once per interview, by the context
 *  that owns the socket. */
export function setEndpointEventSink(next) {
    sink = next;
}
export function reportEndpointEvent(event, extra = {}) {
    if (!sink)
        return;
    try {
        if (!sink.active())
            return;
        sink.send(Object.assign({ event, ts: sink.elapsedSec(), sessionId: pageSessionId() }, extra));
    }
    catch (_a) {
        // Reporting must never break the thing it is observing.
    }
}
/**
 * Attach the window-level listeners. Returns a detach function.
 *
 * The distinctions here are deliberate and are what make the events worth
 * having:
 *
 *   tab_switch vs blur       a TAB losing foreground and a WINDOW losing focus
 *                            are different events with different meanings. A
 *                            candidate alt-tabbing to another app blurs the
 *                            window without hiding the tab.
 *   visibility_hidden alone  hidden WITHOUT a tab switch is the signature of a
 *                            virtual-desktop or Spaces switch — the combination
 *                            is the detection, which is why they are not merged.
 *   blur duration            two seconds is a notification; forty is somewhere
 *                            else entirely. Occurrence alone says almost
 *                            nothing.
 *   resize                   split-screen and picture-in-picture arrangements
 *                            are a window-geometry story before they are
 *                            anything else.
 */
export function attachEndpointEvents() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => undefined;
    }
    let blurStartedAt = null;
    let resizeTimer = null;
    const onVisibility = () => {
        if (document.hidden) {
            reportEndpointEvent('visibility_hidden');
        }
        else {
            reportEndpointEvent('visibility_visible');
        }
    };
    const onBlur = () => {
        blurStartedAt = Date.now();
        reportEndpointEvent('blur');
    };
    const onFocus = () => {
        // Duration is measured in WALL time deliberately: the question is how long
        // the candidate was away, and the recorder may legitimately have been
        // paused for part of it.
        const durationSec = blurStartedAt === null ? undefined : (Date.now() - blurStartedAt) / 1000;
        blurStartedAt = null;
        // `!= null`, not a truthiness check: a blur and focus inside the same
        // millisecond gives 0, and `0` is falsy — which would drop the duration for
        // exactly the events where "they came straight back" is the finding.
        reportEndpointEvent('focus', durationSec != null ? { durationSec } : {});
    };
    const onResize = () => {
        // Debounced: a drag produces a resize event per frame, and 60 of them say
        // nothing the last one does not.
        if (resizeTimer)
            clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => reportEndpointEvent('resize'), 400);
    };
    const onFullscreen = () => {
        if (!document.fullscreenElement)
            reportEndpointEvent('fullscreen_exit');
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('resize', onResize);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => {
        if (resizeTimer)
            clearTimeout(resizeTimer);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('blur', onBlur);
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('resize', onResize);
        document.removeEventListener('fullscreenchange', onFullscreen);
    };
}
